import { useEffect, useState } from "react";
import { Button } from "@/components/ui/shadcn/button";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "@/components/ui/shadcn/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/shadcn/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/shadcn/alert-dialog";
import { quotesAPI } from "@/services/api";
import { Quote, QuoteUnit, QuoteSide, QuoteStatus, DateEnhancedQuote } from "@/types/quotes";
import { Asset } from "@/types/assets";
import { CheckIcon, ClipboardIcon, XCircleIcon, ChevronDownIcon } from "lucide-react";
import { DateTime } from "luxon";
import {
  getFormattedTimeUntil,
  formatAssetAmount,
} from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";
import { truncateAddress } from "@/utils/crypto-address";
import {
  SortableTableHeader,
  useTableSorting,
} from "@/components/dashboard/data-table/SortableTableHeader";
import { useColumnVisibility } from "@/components/dashboard/data-table/ColumnVisibility";
import { BaseDataTable } from "@/components/dashboard/data-table/BaseDataTable";

const publicHttpUrl =
    import.meta.env.VITE_PUBLIC_HTTP_URL || "http://localhost:5173";

interface Column {
  id: string;
  label: string;
}

const DEFAULT_COLUMNS: Column[] = [
  { id: "client_name", label: "Client Name" },
  { id: "asset_pair", label: "Asset Pair" },
  { id: "side", label: "Side" },
  { id: "amount", label: "Amount" },
  { id: "initial_price", label: "Initial Price" },
  { id: "latest_prices", label: "Latest Price" },
  { id: "status", label: "Status" },
  { id: "expiration_date", label: "Expiration" },
];

// Define the sortable keys type
type SortableKey =
  | keyof Omit<Quote, "client" | "crypto_currency">
  | "client_name"
  | "asset_pair"
  | "expiration_date"
  | "status"
  | "side";

// Define column visibility configuration
const initialColumnVisibility = {
  client_name: { label: "Client", visible: true },
  asset_pair: { label: "Asset Pair", visible: true },
  amount: { label: "Amount", visible: true },
  side: { label: "Side", visible: true },
  initial_price: { label: "Initial Price", visible: true },
  latest_prices: { label: "Last Seen Price", visible: true },
  status: { label: "Status", visible: true },
  expiration_date: { label: "Expires in", visible: true },
};

export function ActiveQuotesTab({
  handleExportCSV,
  onQuoteCreated,
  assets,
}: {
  handleExportCSV: () => void;
  onQuoteCreated: () => void;
  assets: Asset[];
}) {
  const { toast } = useToast();
  const [quotes, setQuotes] = useState<DateEnhancedQuote[]>([]);
  const [filteredQuotes, setFilteredQuotes] = useState<DateEnhancedQuote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedURL, setCopiedURL] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<QuoteStatus[]>([
    QuoteStatus.ACTIVE,
  ]);
  const [selectedVmarketIds, setSelectedVmarketIds] = useState<number[]>([]);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [quoteToCancel, setQuoteToCancel] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  // const [visibleColumns, setVisibleColumns] = useState<Column[]>(DEFAULT_COLUMNS);
  const [copiedAsset, setCopiedAsset] = useState<string | null>(null);

  // Use the new sorting hook
  const { sortConfig, handleSort } = useTableSorting<SortableKey>(
    "expiration_date",
    "asc"
  );

  // Use the column visibility hook with a storage key
  const { columns, setColumns, isColumnVisible } = useColumnVisibility(
    initialColumnVisibility,
    "active-quotes-column-visibility"
  );

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedVmarketIds, selectedStatuses]);

  const fetchQuotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await quotesAPI.getQuotes({
        page,
        size,
        vmarket_ids: selectedVmarketIds.length > 0 ? selectedVmarketIds : undefined,
        status: selectedStatuses,
        start_date: startDate ? startDate.toISOString() : undefined,
        end_date: endDate ? endDate.toISOString() : undefined,
      });

      // Enhance quotes with Date objects and numeric amount for easier sorting
      const enhancedQuotes = response.items.map((quote) => ({
        ...quote,
        expiration_date: DateTime.fromISO(quote.expiration).toJSDate(),
        amount_numeric: typeof quote.amount === "string" ? parseFloat(quote.amount) : quote.amount,
      }));

      // Set the quotes directly from the backend response
      setQuotes(enhancedQuotes);
      setFilteredQuotes(enhancedQuotes);
      setTotal(response.total);
      setPages(response.pages);

      if (import.meta.env.DEV) {
        console.log("Quotes fetched successfully:", {
          count: response.items.length,
          total: response.total,
          page,
          size,
          pages: response.pages,
          selectedVmarketIds,
          selectedStatuses,
          startDate,
          endDate,
        });
      }
    } catch (err) {
      handleApiError("Fetch Quotes", err, toast);
      setError("Failed to fetch quotes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [page, size, selectedVmarketIds, selectedStatuses]);

  const handleCopyToClipboard = (quoteUrl: string) => {
    navigator.clipboard
      .writeText(quoteUrl)
      .then(() => {
        setCopiedURL(quoteUrl);
        showSuccessToast(toast, "Copied", "Quote link copied to clipboard");
        setTimeout(() => setCopiedURL(null), 2000);
      })
      .catch((error) => {
        console.error("Failed to copy link:", error);
        toast({
          title: "Error",
          description: "Failed to copy link to clipboard",
          variant: "error",
        });
      });
  };

  const handleQuoteCreated = (newQuote: Quote) => {
    // Add the enhanced properties to be consistent with existing quotes
    const enhancedQuote = {
      ...newQuote,
      expiration_date: DateTime.fromISO(newQuote.expiration).toJSDate(),
      amount_numeric:
        typeof newQuote.amount === "string"
          ? parseFloat(newQuote.amount)
          : newQuote.amount,
    };

    // Update quotes state - this will trigger the useEffect which handles filtering and sorting
    setQuotes((prevQuotes) => [...prevQuotes, enhancedQuote]);

    // Call the parent's onQuoteCreated handler
    onQuoteCreated();
  };

  const handleCancelQuote = async (slug: string) => {
    try {
      await quotesAPI.cancelQuote(slug);
      showSuccessToast(toast, "Success", "Quote cancelled successfully");
      // Refresh the quotes list
      fetchQuotes();
    } catch (err) {
      handleApiError("Cancel Quote", err, toast);
    } finally {
      setQuoteToCancel(null);
    }
  };

  const toggleRow = (slug: string) => {
    setExpandedRow(expandedRow === slug ? null : slug);
  };

  const getAssetByCode = (code: string) => {
    return assets.find(asset => asset.code === code);
  };

  const renderTableHeader = ({
    sortConfig,
    handleSort,
    isColumnVisible,
  }: {
    sortConfig: { key: string; direction: "asc" | "desc" | null };
    handleSort: (key: string) => void;
    isColumnVisible: (key: string) => boolean;
  }) => (
    <>
      {isColumnVisible("client_name") && (
        <TableHead>
          <SortableTableHeader
            label="Client"
            sortKey="client_name"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("asset_pair") && (
        <TableHead>
          <SortableTableHeader
            label="Asset Pair"
            sortKey="asset_pair"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("side") && (
        <TableHead>
          <SortableTableHeader
            label="Side"
            sortKey="side"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("amount") && <TableHead>Amount</TableHead>}
      {isColumnVisible("initial_price") && <TableHead>Initial Price</TableHead>}
      {isColumnVisible("latest_prices") && (
        <TableHead>Last Seen Price</TableHead>
      )}
      {isColumnVisible("status") && (
        <TableHead>
          <SortableTableHeader
            label="Status"
            sortKey="status"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("expiration_date") && (
        <TableHead>
          <SortableTableHeader
            label="Expires in"
            sortKey="expiration_date"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
    </>
  );

  const renderTableRow = (quote: DateEnhancedQuote) => {
    const expirationTime = DateTime.fromISO(quote.expiration, {
      zone: "utc",
    });
    const now = DateTime.utc();
    const timeRemaining = getFormattedTimeUntil(now, expirationTime);
    const quoteUrl = `${publicHttpUrl}/client/accept-quote/${quote.slug}`;
    const isExpanded = expandedRow === quote.slug;

    return (
      <>
        <TableRow
          key={quote.slug}
          className={`hover:bg-gray-100 transition cursor-pointer ${
            isExpanded ? "bg-gray-100" : ""
          }`}
          onClick={() => toggleRow(quote.slug)}
        >
          {isColumnVisible("client_name") && (
            <TableCell>
              <span className="cursor-pointer underline decoration-dotted">
                {quote.client.name}
              </span>
            </TableCell>
          )}
          {isColumnVisible("asset_pair") && (
            <TableCell>
              {`${quote.vmarket.base.code}/${quote.vmarket.counter.code}`}
            </TableCell>
          )}
          {isColumnVisible("side") && (
            <TableCell>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  quote.side === QuoteSide.BUY
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {quote.side}
              </span>
            </TableCell>
          )}
          {isColumnVisible("amount") && (
            <TableCell>
              {formatAssetAmount(
                quote.amount.toString(),
                quote.unit === QuoteUnit.BASE
                  ? quote.vmarket.base
                  : quote.vmarket.counter
              )}{" "}
              {quote.unit === QuoteUnit.BASE
                ? quote.vmarket.base.code
                : quote.vmarket.counter.code}
            </TableCell>
          )}
          {isColumnVisible("initial_price") && (
            <TableCell>
              {quote.initial_price
                ? `${quote.vmarket.counter.code} ${formatAssetAmount(
                    quote.initial_price.price_with_fee,
                    quote.vmarket.counter
                  )}`
                : "---"}
            </TableCell>
          )}
          {isColumnVisible("latest_prices") && (
            <TableCell>
              {quote.latest_price
                ? `${quote.vmarket.counter.code} ${formatAssetAmount(
                    quote.latest_price.price_with_fee,
                    quote.vmarket.counter
                  )}`
                : "---"}
            </TableCell>
          )}
          {isColumnVisible("status") && (
            <TableCell>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  quote.status === QuoteStatus.ACTIVE
                    ? "bg-green-100 text-green-700"
                    : quote.status === QuoteStatus.ACCEPTED
                    ? "bg-blue-100 text-blue-700"
                    : quote.status === QuoteStatus.CANCELLED
                    ? "bg-red-100 text-red-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {quote.status}
              </span>
            </TableCell>
          )}
          {isColumnVisible("expiration_date") && (
            <TableCell>
              {timeRemaining.includes("-") ? (
                <span className="text-red-700">
                  {timeRemaining.replace("-", "")} ago
                </span>
              ) : (
                <span>{timeRemaining}</span>
              )}
            </TableCell>
          )}
          <TableCell className="w-0">
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform ${
                isExpanded ? "transform rotate-180" : ""
              }`}
            />
          </TableCell>
        </TableRow>
        {isExpanded && (
          <TableRow className="bg-gray-50">
            <TableCell colSpan={9}>
              <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                {/* Header with Status Badge */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Quote Details
                  </h3>
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full ${
                      quote.status === QuoteStatus.ACTIVE
                        ? "bg-green-100 text-green-700"
                        : quote.status === QuoteStatus.ACCEPTED
                        ? "bg-blue-100 text-blue-700"
                        : quote.status === QuoteStatus.CANCELLED
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {quote.status}
                  </span>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Left Column - Information Cards Side by Side */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Client Information Card */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">
                          Client Information
                        </h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">Name:</span>
                          <span className="font-medium text-gray-900">
                            {quote.client.name}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">Email:</span>
                          <span className="font-medium text-gray-900">
                            {quote.client.email}
                          </span>
                        </div>
                        {quote.client.phone && (
                          <div className="flex items-center">
                            <span className="text-gray-500 w-14">Phone:</span>
                            <span className="font-medium text-gray-900">
                              {quote.client.phone}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Quote Information Card */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <svg
                            className="w-4 h-4 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">
                          Quote Details
                        </h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            Asset Pair:
                          </span>
                          <span className="font-medium text-gray-900">
                            {quote.vmarket.base.code}/
                            {quote.vmarket.counter.code}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">Amount:</span>
                          <span className="font-medium text-gray-900">
                            {formatAssetAmount(
                              quote.amount.toString(),
                              quote.unit === QuoteUnit.BASE
                                ? quote.vmarket.base
                                : quote.vmarket.counter
                            )}{" "}
                            {quote.unit === QuoteUnit.BASE
                              ? quote.vmarket.base.code
                              : quote.vmarket.counter.code}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">Side:</span>
                          <span className="font-medium text-gray-900">
                            {quote.side.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            Initial Price:
                          </span>
                          <span className="font-medium text-gray-900">
                            {quote.initial_price
                              ? `${quote.vmarket.counter.code} ${formatAssetAmount(
                                  quote.initial_price.price_with_fee,
                                  quote.vmarket.counter
                                )}`
                              : "---"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - URL and Actions */}
                  <div className="space-y-4">
                    {/* Quote URL Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <svg
                            className="w-4 h-4 text-purple-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                            />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">
                          Quote URL
                        </h4>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-lg py-2 px-3 border border-gray-200">
                        <div className="flex-1 font-mono text-[0.9rem] text-gray-600 break-all mr-4 py-2 mt-1">
                          <a
                            href={quoteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {truncateAddress(quoteUrl, 19, 8)}
                          </a>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyToClipboard(quoteUrl);
                              }}
                              className="text-[0.8rem] max-w-[100px] max-h-[10px] bg-white hover:bg-gray-50 p-4"
                            >
                              {copiedURL === quoteUrl ? (
                                <span className="flex items-center">
                                  <CheckIcon className="max-w-[12px] max-h-[12px] mr-2 text-green-500" />{" "}
                                  Copied
                                </span>
                              ) : (
                                <span className="flex items-center">
                                  <ClipboardIcon className="max-w-[12px] max-h-[12px] mr-2" />{" "}
                                  Copy URL
                                </span>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copy quote URL to clipboard</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    {/* Actions Section */}
                    {quote.status === QuoteStatus.ACTIVE && (
                      <div className="flex justify-end">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuoteToCancel(quote.slug);
                              }}
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            >
                              <XCircleIcon className="h-4 w-4 mr-2" />
                              Cancel Quote
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Cancel this quote</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <div className="space-y-4">
      <BaseDataTable
        title="Active Quotes"
        description="Manage and create new quotes"
        data={quotes}
        filteredData={filteredQuotes}
        setFilteredData={setFilteredQuotes}
        loading={loading}
        error={error}
        onRetry={fetchQuotes}
        onExportCSV={handleExportCSV}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedVmarketIds={selectedVmarketIds}
        setSelectedVmarketIds={setSelectedVmarketIds}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        columns={columns}
        default_columns={initialColumnVisibility}
        storageKey="active-quotes-column-visibility"
        setColumns={setColumns}
        sortableKeys={[
          "client_name",
          "asset_pair",
          "expiration_date",
          "status",
        ]}
        defaultSortKey="expiration_date"
        defaultSortDirection="asc"
        renderTableHeader={renderTableHeader}
        renderTableRow={renderTableRow}
        withQuoteTypeBoxes={true}
        showCreateQuoteButton={true}
        onQuoteCreated={handleQuoteCreated}
        pagination={{
          page,
          setPage,
          size,
          setSize,
          total,
          pages,
        }}
        onResetColumns={fetchQuotes}
      />

      <AlertDialog
        open={!!quoteToCancel}
        onOpenChange={(open) => !open && setQuoteToCancel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Quote</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this quote? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => quoteToCancel && handleCancelQuote(quoteToCancel)}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Cancel Quote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
